/**
 * Filter Entities API
 * Provides functions to load entities for dashboard filtering
 * Data source: monitor database
 */

import { buildConfigApiUrl } from '../api';

const getHeaders = () => ({
  'Content-Type': 'application/json',
});

/**
 * Load hosts for filtering
 * @param {number} [hostGroupId] - Optional host group ID to filter hosts by
 * @param {number} [businessServiceId] - Optional business service ID to filter hosts by
 * @returns {Promise<Array>} Array of {id, name} objects
 */
export async function loadHosts(hostGroupId, businessServiceId) {
  try {
    let url = '/filters/hosts';
    const params = [];
    if (hostGroupId) {
      params.push(`hostGroupIds=${encodeURIComponent(hostGroupId)}`);
    }
    if (businessServiceId) {
      params.push(`businessServiceIds=${encodeURIComponent(businessServiceId)}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    const response = await fetch(buildConfigApiUrl(url), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error('Error loading hosts:', error);
    return [];
  }
}

/**
 * Generic helper to load hosts for multiple entity IDs in parallel
 * @param {Array<number>} ids - Array of entity IDs
 * @param {Function} loadHostFn - Function that loads hosts for a single entity ID
 * @param {string} entityName - Entity name for error logging
 * @returns {Promise<Array>} Array of {id, name} objects (deduplicated)
 */
const loadHostsForEntities = async (ids, loadHostFn, entityName) => {
  if (!ids || ids.length === 0) {
    return [];
  }
  try {
    const results = await Promise.all(ids.map(loadHostFn));
    const allHosts = results.flat();
    return Array.from(new Map(allHosts.map((h) => [h.id, h])).values());
  } catch (error) {
    console.error(`Error loading hosts for ${entityName}:`, error);
    return [];
  }
};

/**
 * Load hosts for multiple host groups in parallel
 * @param {Array<number>} hostGroupIds - Array of host group IDs
 * @returns {Promise<Array>} Array of {id, name} objects (deduplicated)
 */
export const loadHostsForHostGroups = (hostGroupIds) =>
  loadHostsForEntities(hostGroupIds, (id) => loadHosts(id), 'host groups');

/**
 * Load hosts for multiple business services in parallel
 * @param {Array<number>} businessServiceIds - Array of business service IDs
 * @returns {Promise<Array>} Array of {id, name} objects (deduplicated)
 */
export const loadHostsForBusinessServices = (businessServiceIds) =>
  loadHostsForEntities(businessServiceIds, (id) => loadHosts(null, id), 'business services');

/**
 * Load metrics for specific hosts
 * @param {Array<Object>} hosts - Array of host objects with {id, name}
 * @returns {Promise<Array>} Array of {id, name, hostId, hostName} objects
 */
export async function loadMetricsForHosts(hosts = []) {
  try {
    if (!hosts || hosts.length === 0) {
      return [];
    }

    // Make parallel requests for each host using the hostId query parameter
    const metricsPromises = hosts.map(async (host) => {
      const hostId = host.id;
      const url = buildConfigApiUrl(`/filters/metrics?hostIds=${encodeURIComponent(hostId)}`);
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      return response.json();
    });

    // Wait for all requests to complete and flatten results
    const results = await Promise.all(metricsPromises);
    return results.flat();
  } catch (error) {
    console.error('Error loading metrics for hosts:', error);
    return [];
  }
}

/**
 * Load metrics for a specific metric category
 * @param {number} metricCategoryId - Metric category ID to filter metrics by
 * @returns {Promise<Array>} Array of {id, name, hostId, hostName} objects
 */
export async function loadMetricsForCategory(metricCategoryId) {
  try {
    if (!metricCategoryId) {
      return [];
    }

    const url = buildConfigApiUrl(`/filters/metrics?metricCategoryId=${encodeURIComponent(metricCategoryId)}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return (await response.json()) || [];
  } catch (error) {
    console.error('Error loading metrics for category:', error);
    return [];
  }
}

/**
 * Load metric categories for filtering
 * @returns {Promise<Array>} Array of {id, name} objects
 */
export async function loadMetricCategories() {
  try {
    const response = await fetch(buildConfigApiUrl('/filters/metric-categories'), {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error('Error loading metric categories:', error);
    return [];
  }
}

/**
 * Load host categories for filtering
 * @returns {Promise<Array>} Array of {id, name} objects
 */
export async function loadHostCategories() {
  try {
    const response = await fetch(buildConfigApiUrl('/filters/host-categories'), {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error('Error loading host categories:', error);
    return [];
  }
}

/**
 * Load all metrics for filtering (with host information)
 * When called without parameters, loads ALL metrics (used for displaying selected metrics)
 * When called with hostIds or businessServiceIds, loads metrics filtered by those
 * @param {Object} options - Filter options
 * @param {Array<number>} options.hostIds - Optional array of host IDs to filter metrics by
 * @param {Array<number>} options.businessServiceIds - Optional array of business service IDs to filter metrics by
 * @param {Array<string>} options.metricStatuses - Optional array of metric status IDs to filter by
 * @param {Array<number>} options.metricCategories - Optional array of metric category IDs to filter by
 * @returns {Promise<Array>} Array of {id, name, hostId, hostName} objects
 */
export async function loadMetrics({ hostIds = [], businessServiceIds = [], metricStatuses = [], metricCategories = [] } = {}) {
  try {
    const params = [];

    // Add hostIds as comma-separated list
    if (hostIds && hostIds.length > 0) {
      params.push(`hostIds=${hostIds.map((id) => encodeURIComponent(id)).join(',')}`);
    }

    // Add businessServiceIds as comma-separated list
    if (businessServiceIds && businessServiceIds.length > 0) {
      params.push(`businessServiceIds=${businessServiceIds.map((id) => encodeURIComponent(id)).join(',')}`);
    }

    // Add metricStatuses as comma-separated list
    if (metricStatuses && metricStatuses.length > 0) {
      params.push(`metricStatuses=${metricStatuses.map((id) => encodeURIComponent(id)).join(',')}`);
    }

    // Add metricCategories as comma-separated list
    if (metricCategories && metricCategories.length > 0) {
      params.push(`metricCategories=${metricCategories.map((id) => encodeURIComponent(id)).join(',')}`);
    }

    const url = params.length > 0
      ? buildConfigApiUrl(`/filters/metrics?${params.join('&')}`)
      : buildConfigApiUrl('/filters/metrics');

    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return (await response.json()) || [];
  } catch (error) {
    console.error('Error loading metrics:', error);
    return [];
  }
}

/**
 * Load hosts with all filters ANDed together
 * @param {Object} options - Filter options (all ANDed together)
 * @param {Array<number>} options.hostIds - Optional array of host IDs to filter by
 * @param {Array<number>} options.hostGroupIds - Optional array of host group IDs to filter by
 * @param {Array<number>} options.businessServiceIds - Optional array of business service IDs to filter by
 * @param {Array<string>} options.hostStatuses - Optional array of host status IDs to filter by
 * @param {Array<number>} options.hostCategories - Optional array of host category IDs to filter by
 * @returns {Promise<Array>} Array of {id, name} objects
 */
export async function loadHostsFiltered({ hostIds = [], hostGroupIds = [], businessServiceIds = [], hostStatuses = [], hostCategories = [] } = {}) {
  try {
    const params = [];

    // Add hostIds as comma-separated list
    if (hostIds && hostIds.length > 0) {
      params.push(`hostIds=${hostIds.map((id) => encodeURIComponent(id)).join(',')}`);
    }

    // Add hostGroupIds as comma-separated list
    if (hostGroupIds && hostGroupIds.length > 0) {
      params.push(`hostGroupIds=${hostGroupIds.map((id) => encodeURIComponent(id)).join(',')}`);
    }

    // Add businessServiceIds as comma-separated list
    if (businessServiceIds && businessServiceIds.length > 0) {
      params.push(`businessServiceIds=${businessServiceIds.map((id) => encodeURIComponent(id)).join(',')}`);
    }

    // Add hostStatuses as comma-separated list
    if (hostStatuses && hostStatuses.length > 0) {
      params.push(`hostStatuses=${hostStatuses.map((id) => encodeURIComponent(id)).join(',')}`);
    }

    // Add hostCategories as comma-separated list
    if (hostCategories && hostCategories.length > 0) {
      params.push(`hostCategories=${hostCategories.map((id) => encodeURIComponent(id)).join(',')}`);
    }

    const url = params.length > 0
      ? buildConfigApiUrl(`/filters/hosts?${params.join('&')}`)
      : buildConfigApiUrl('/filters/hosts');

    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return (await response.json()) || [];
  } catch (error) {
    console.error('Error loading filtered hosts:', error);
    return [];
  }
}

/**
 * Load business services for filtering
 * @returns {Promise<Array>} Array of {id, name} objects
 */
export async function loadBusinessServices() {
  try {
    const response = await fetch(buildConfigApiUrl('/filters/business-services'), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error('Error loading business services:', error);
    return [];
  }
}

/**
 * Load host groups for filtering
 * @returns {Promise<Array>} Array of {id, name} objects
 */
export async function loadHostGroups() {
  try {
    const response = await fetch(buildConfigApiUrl('/filters/host-groups'), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error('Error loading host groups:', error);
    return [];
  }
}

/**
 * Load all filter entities at once (more efficient - single API call)
 * @returns {Promise<Object>} Object with hosts, metrics, business_services, host_groups, metric_categories arrays
 */
export async function loadAllFilterEntities() {
  try {
    const response = await fetch(buildConfigApiUrl('/filters/all'), {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();

    return {
      hosts: data.hosts || [],
      metrics: data.metrics || [],
      business_services: data.business_services || [],
      host_groups: data.host_groups || [],
      metric_categories: data.metric_categories || [],
    };
  } catch (error) {
    console.error('Error loading all filter entities:', error);
    return {
      hosts: [],
      metrics: [],
      business_services: [],
      host_groups: [],
      metric_categories: [],
    };
  }
}

/**
 * Load host timezones for specific host IDs
 * Used for "host timezone" display option in reports
 * @param {Array<number>} hostIds - Array of host IDs to get timezones for
 * @returns {Promise<Object>} Object mapping host_id to timezone offset (e.g., { "123": "+5", "456": "-8" })
 */
export async function loadHostTimezones(hostIds) {
  try {
    if (!hostIds || hostIds.length === 0) {
      return {};
    }

    const url = buildConfigApiUrl(`/filters/host-timezones?hostIds=${encodeURIComponent(hostIds.join(','))}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return (await response.json()) || {};
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error loading host timezones:', error);
    return {};
  }
}

/**
 * Load host timezones for specific host names
 * Used for "host timezone" display option in reports when host_id is not available
 * @param {Array<string>} hostNames - Array of host names to get timezones for
 * @returns {Promise<Object>} Object mapping host_name to timezone offset (e.g., { "server1": "+5", "server2": "-8" })
 */
export async function loadHostTimezonesByName(hostNames) {
  try {
    if (!hostNames || hostNames.length === 0) {
      return {};
    }

    const url = buildConfigApiUrl(`/filters/host-timezones-by-name?hostNames=${encodeURIComponent(hostNames.join(','))}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return (await response.json()) || {};
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error loading host timezones by name:', error);
    return {};
  }
}
